<script lang="js" setup>
import { ref, computed } from 'vue'

const currentTestimonial = ref(0)
import testimonial1 from "@/assets/images/testimonial1.png"
import testimonial2 from "@/assets/images/testimonial2.png"
import testimonial3 from "@/assets/images/testimonial3.png"

const testimonials = [
  {
    id: 1,
    name: 'Michael',
    title: 'Team Lead',
    image: testimonial1,
    testimonialTitle: 'Perfect for Quick Meeting Feedback',
    testimonialText: 'The seamless integration with Google Chat makes Chat-Polls the perfect tool for quick feedback during meetings. Highly recommend!.'
  },
  {
    id: 2,
    name: 'Sarah',
    title: 'Product Manager',
    image: testimonial2,
    testimonialTitle: 'Streamlined Decision Making',
    testimonialText: 'Chat-Polls has revolutionized how we make decisions in our remote team. The real-time polling feature saves us hours of back-and-forth discussions.'
  },
  {
    id: 3,
    name: 'David',
    title: 'Engineering Manager',
    image: testimonial3,
    testimonialTitle: 'Efficient Team Coordination',
    testimonialText: 'The scheduling and automation features are game-changers. We can now gather feedback from our global team without worrying about time zones.'
  }
]

const nextTestimonial = () => {
  currentTestimonial.value = (currentTestimonial.value + 1) % testimonials.length
}

const prevTestimonial = () => {
  currentTestimonial.value = currentTestimonial.value === 0
    ? testimonials.length - 1
    : currentTestimonial.value - 1
}

const activeTestimonial = computed(() => testimonials[currentTestimonial.value])
</script>

<template>
  <!-- Main testimonial container -->
  <div class="relative">
    <div
      class="relative rounded-[24px] overflow-hidden transition-all duration-500 ease-in-out bg-[#282644] before:absolute before:z-10 before:h-[200px] before:w-[300px] before:-left-20 before:-top-16 before:rounded-full before:bg-[#43BFE0] before:opacity-50 before:blur-[100px] after:absolute after:z-10 after:h-[200px] after:w-[300px] after:-right-20 after:-bottom-16 after:rounded-full after:bg-[#AF56FE] after:opacity-50 after:blur-[100px]"
    >
      <div class="relative z-20 px-8 py-12 lg:px-12 lg:py-16">
        <!-- Quote marks - top right -->
        <div class="absolute top-8 right-8 lg:top-12 lg:right-12">
          <svg
            width="32"
            height="28"
            viewBox="0 0 32 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3333 28V17.5C13.3333 11.7917 15.3333 7.29167 19.3333 3.95833C23.3333 0.625 28.6667 -0.791667 35.3333 -0.625V3.5C31.3333 3.5 28.3333 4.41667 26.3333 6.25C24.3333 8.08333 23.3333 10.5 23.3333 13.5H29.3333V28H13.3333ZM-2.66667 28V17.5C-2.66667 11.7917 -0.666667 7.29167 3.33333 3.95833C7.33333 0.625 12.6667 -0.791667 19.3333 -0.625V3.5C15.3333 3.5 12.3333 4.41667 10.3333 6.25C8.33333 8.08333 7.33333 10.5 7.33333 13.5H13.3333V28H-2.66667Z"
              fill="white"
              fill-opacity="0.3"
            />
          </svg>
        </div>

        <!-- Main horizontal layout -->
        <div class="flex flex-col items-center gap-8 lg:gap-12">
          <!-- Profile section - compact left side -->
          <div class="flex w-full items-center gap-x-4">
            <div
              class="h-16 w-16 shrink-0 overflow-hidden rounded-full lg:h-20 lg:w-20"
            >
              <img
                :src="activeTestimonial.image"
                :alt="activeTestimonial.name"
                class="h-full w-full object-cover"
              />
            </div>
            <div class="text-left">
              <h3
                class="mb-1 font-bold text-white text-lg lg:text-xl"
                style="font-family: 'Onest', sans-serif"
              >
                {{ activeTestimonial.name }}
              </h3>
              <p
                class="text-white/70 text-sm lg:text-base"
                style="font-family: 'Rubik', sans-serif"
              >
                {{ activeTestimonial.title }}
              </p>
            </div>
          </div>

          <!-- Testimonial content - main right side -->
          <div class="min-w-0 flex-1">
            <h2
              class="mb-4 font-semibold leading-tight text-white text-xl lg:mb-6 lg:text-2xl xl:text-3xl"
              style="font-family: 'Poppins', sans-serif"
            >
              {{ activeTestimonial.testimonialTitle }}
            </h2>
            <p
              class="leading-relaxed text-white/90 text-base lg:text-lg xl:text-xl"
              style="font-family: 'Onest', sans-serif"
            >
              "{{ activeTestimonial.testimonialText }}"
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation arrows - desktop only (hidden on mobile) -->
    <div class="absolute top-1/2 -left-20 hidden -translate-y-1/2 lg:block">
      <button
        @click="prevTestimonial"
        class="flex h-16 w-16 items-center justify-center rounded-full bg-black/20 backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-black/30"
        style="backdrop-filter: blur(10px)"
      >
        <svg
          width="12"
          height="20"
          viewBox="0 0 14 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="rotate-180"
        >
          <path
            d="M1 1L12 11.5L1 22"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div class="absolute top-1/2 -right-20 hidden -translate-y-1/2 lg:block">
      <button
        @click="nextTestimonial"
        class="flex h-16 w-16 items-center justify-center rounded-full bg-black/20 backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-black/30"
        style="backdrop-filter: blur(10px)"
      >
        <svg
          width="12"
          height="20"
          viewBox="0 0 14 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L12 11.5L1 22"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Mobile navigation and dots container -->
    <div class="mt-8 flex items-center justify-center gap-6">
      <!-- Mobile left arrow -->
      <button
        @click="prevTestimonial"
        class="flex h-10 w-10 items-center justify-center rounded-full bg-black/20 backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-black/30 lg:hidden"
        style="backdrop-filter: blur(10px)"
      >
        <svg
          width="10"
          height="16"
          viewBox="0 0 14 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="rotate-180"
        >
          <path
            d="M1 1L12 11.5L1 22"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- Dots indicator -->
      <div class="flex justify-center gap-3">
        <button
          v-for="(testimonial, index) in testimonials"
          :key="testimonial.id"
          @click="currentTestimonial = index"
          :class="[
            'w-2 h-2 lg:w-3 lg:h-3 rounded-full transition-all duration-300',
            index === currentTestimonial
              ? 'bg-white scale-125'
              : 'bg-white/40 hover:bg-white/60',
          ]"
        />
      </div>

      <!-- Mobile right arrow -->
      <button
        @click="nextTestimonial"
        class="flex h-10 w-10 items-center justify-center rounded-full bg-black/20 backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-black/30 lg:hidden"
        style="backdrop-filter: blur(10px)"
      >
        <svg
          width="10"
          height="16"
          viewBox="0 0 14 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L12 11.5L1 22"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>
